import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Badge,
  CheckCircle,
  EventAvailable,
  FileDownload,
  FilterAltOff,
  MedicalInformation,
  RadioButtonUnchecked,
  Warning,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, {
  ITENS_POR_PAGINA_PADRAO,
} from "../components/Pagination/StandardPagination";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import {
  patientTrackingService,
  PatientTrackingItem,
  PatientTrackingSummary,
} from "../services/patientTrackingService";

// Acompanhamento de processos do paciente — insumo da coordenação. Mostra a EXISTÊNCIA do
// registro, nunca o conteúdo clínico (RN-540).

const SITUATIONS = [
  { value: "tracked", label: "Em acompanhamento" },
  { value: "inactive", label: "Sem atendimento" },
  { value: "never", label: "Nunca atendido" },
  { value: "", label: "Todos" },
];

// Cada pendência tem um dono: é o que transforma o relatório em tarefa distribuível.
const PENDING = [
  { value: "", label: "Todas as pendências", group: "" },
  { value: "register", label: "Cadastro incompleto", group: "Secretaria" },
  { value: "photo", label: "Sem foto", group: "Secretaria" },
  { value: "link", label: "Sem vínculo registrado", group: "Secretaria" },
  { value: "anamnesis", label: "Sem anamnese", group: "Profissional" },
  { value: "diagnosis", label: "Sem diagnóstico", group: "Profissional" },
  { value: "plan", label: "Sem plano terapêutico", group: "Profissional" },
  { value: "evaluation", label: "Sem nenhuma avaliação", group: "Profissional" },
  { value: "evolution", label: "Evolução atrasada", group: "Coordenação" },
  { value: "presence", label: "Sem presença no mês", group: "Coordenação" },
  { value: "schedule", label: "Sem agendamento futuro", group: "Coordenação" },
  { value: "evaluationOverdue", label: "Reavaliação vencida", group: "Coordenação" },
];

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

/**
 * Uma definição por coluna, usada no cabeçalho E na célula. Antes cada lado declarava o seu
 * `flex` e eles saíam de sincronia — foi o que desalinhou a tabela.
 * `c4u-col--*` é a ordem de sacrifício quando a tela estreita.
 */
const COLUNAS = [
  { titulo: "Paciente", flex: "1 1 200px", classe: "" },
  { titulo: "Identificação", flex: "0 0 120px", classe: "" },
  { titulo: "Base clínica", flex: "0 0 120px", classe: "" },
  { titulo: "Últ. presença", flex: "0 0 100px", classe: "c4u-col--lg" },
  { titulo: "Presenças/mês", flex: "0 0 100px", classe: "c4u-col--md" },
  { titulo: "Últ. evolução", flex: "0 0 100px", classe: "c4u-col--xl" },
  { titulo: "Próx. agenda", flex: "0 0 100px", classe: "c4u-col--lg" },
  { titulo: "Reavaliação", flex: "0 0 105px", classe: "" },
];

const cardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1.5rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
  mb: 3,
};

/**
 * Marca de presença/ausência do registro. O RÓTULO acompanha o ícone: três círculos anônimos não
 * dizem o que são, e no celular não há tooltip para explicar (achado do Hilton, 08/09/2026).
 * No desktop o rótulo fica só para leitor de tela e para o cartão do celular.
 */
const Marca: React.FC<{ ok: boolean; titulo: string; critico?: boolean }> = ({ ok, titulo, critico }) => (
  <Tooltip title={`${titulo}: ${ok ? "sim" : "não"}`} arrow>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
      {ok ? (
        <CheckCircle sx={{ fontSize: 17, color: "#28a745" }} />
      ) : critico ? (
        <Warning sx={{ fontSize: 17, color: "#dc3545" }} />
      ) : (
        <RadioButtonUnchecked sx={{ fontSize: 17, color: "#ced4da" }} />
      )}
      <Typography
        component="span"
        className="c4u-mark-label"
        sx={{ fontSize: 11, color: "#6c757d", display: { xs: "inline", md: "none" } }}
      >
        {titulo}
      </Typography>
    </Box>
  </Tooltip>
);

const ReportPatientTracking: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const [situation, setSituation] = useState("tracked");
  const [pending, setPending] = useState("");
  const [search, setSearch] = useState("");
  const [cycleMonths, setCycleMonths] = useState(6);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(ITENS_POR_PAGINA_PADRAO);

  const [items, setItems] = useState<PatientTrackingItem[]>([]);
  const [summary, setSummary] = useState<PatientTrackingSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [exporting, setExporting] = useState(false);

  const hasFilterChanges =
    situation !== "tracked" || !!pending || !!search || cycleMonths !== 6;

  const exportar = async () => {
    setExporting(true);
    try {
      const blob = await patientTrackingService.exportXlsx({
        situation,
        pending: pending || undefined,
        search: search.trim() || undefined,
        evaluationCycleMonths: cycleMonths,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `acompanhamento_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível gerar a planilha.");
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await patientTrackingService.get(
        {
          situation,
          pending: pending || undefined,
          search: search.trim() || undefined,
          evaluationCycleMonths: cycleMonths,
        },
        page,
        pageSize,
      );
      setItems(data.items);
      setSummary(data.summary);
      setTotal(data.total);
    } catch {
      setError("Não foi possível carregar o acompanhamento.");
      setItems([]);
      setSummary(null);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [situation, pending, search, cycleMonths, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const clearFilters = () => {
    setSituation("tracked");
    setPending("");
    setSearch("");
    setCycleMonths(6);
    setPage(1);
  };

  const pct = (n: number): string =>
    summary && summary.patients > 0 ? `${Math.round((100 * n) / summary.patients)}%` : "—";

  const blocks = summary
    ? [
        {
          title: "Identificação",
          owner: "Secretaria",
          icon: <Badge fontSize="small" />,
          color: "#0d6e6e",
          rows: [
            { label: "Cadastro completo", value: summary.registerComplete },
            { label: "Com foto", value: summary.withPhoto },
            { label: "Com vínculo", value: summary.withActiveLink },
          ],
        },
        {
          title: "Base clínica",
          owner: "Profissional",
          icon: <MedicalInformation fontSize="small" />,
          color: "#6610f2",
          rows: [
            { label: "Com anamnese", value: summary.withAnamnesis },
            { label: "Com diagnóstico", value: summary.withDiagnosis },
            { label: "Com plano", value: summary.withPlanGoals },
          ],
        },
        {
          title: "Acompanhamento",
          owner: "Coordenação",
          icon: <EventAvailable fontSize="small" />,
          color: "#fd7e14",
          rows: [
            { label: "Evolução em dia", value: summary.evolutionUpToDate },
            { label: "Presença no mês", value: summary.withPresenceLastMonth },
            { label: "Agenda futura", value: summary.nextSchedule },
          ],
        },
      ]
    : [];

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Acompanhamento de processos
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                O que existe e o que falta no prontuário de cada paciente. Mostra que o registro
                existe, nunca o conteúdo clínico.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
              <TextField
                select
                size="small"
                label="Situação do paciente"
                value={situation}
                onChange={(e) => {
                  setSituation(e.target.value);
                  setPage(1);
                }}
                fullWidth
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                {SITUATIONS.map((s) => (
                  <MenuItem key={s.value || "all"} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Pendência"
                value={pending}
                onChange={(e) => {
                  setPending(e.target.value);
                  setPage(1);
                }}
                fullWidth
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                {PENDING.map((p) => (
                  <MenuItem key={p.value || "all"} value={p.value}>
                    {p.group ? `${p.group} · ${p.label}` : p.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Busca por nome"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                fullWidth
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 60 }}
              />
              <TextField
                select
                size="small"
                label="Ciclo de reavaliação"
                value={cycleMonths}
                onChange={(e) => {
                  setCycleMonths(Number(e.target.value));
                  setPage(1);
                }}
                fullWidth
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value={3}>A cada 3 meses</MenuItem>
                <MenuItem value={6}>A cada 6 meses</MenuItem>
                <MenuItem value={12}>A cada 12 meses</MenuItem>
              </TextField>
              </div>

              {/* 2 e 4: limpar e exportar juntos, no fim da barra */}
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <Tooltip title="Baixar planilha do recorte">
                  <span>
                    <IconButton
                      onClick={exportar}
                      disabled={exporting || loading || items.length === 0}
                      sx={{
                        width: 40,
                        height: 40,
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        color: colors.primary,
                      }}
                    >
                      {exporting ? <CircularProgress size={18} /> : <FileDownload sx={{ fontSize: "1.2rem" }} />}
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

          {summary && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 2,
                mb: 2,
              }}
            >
              {blocks.map((b) => (
                <Paper key={b.title} elevation={0} sx={{ ...cardSx, mb: 0, padding: "1rem 1.25rem" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        bgcolor: b.color,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {b.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>{b.title}</Typography>
                      <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>resolve: {b.owner}</Typography>
                    </Box>
                  </Box>
                  {b.rows.map((r) => {
                    const perc = summary.patients > 0 ? Math.round((100 * r.value) / summary.patients) : 0;
                    // verde a partir de 80%, amarelo de 40 a 79, vermelho abaixo: a cor diz
                    // onde agir sem precisar ler o número
                    const cor = perc >= 80 ? "#28a745" : perc >= 40 ? "#fd7e14" : "#dc3545";
                    return (
                      <Box key={r.label} sx={{ mb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                          <Typography sx={{ fontSize: 12.5, color: "#495057" }}>{r.label}</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: cor, fontVariantNumeric: "tabular-nums" }}>
                            {r.value}
                            <Typography component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#adb5bd", ml: 0.5 }}>
                              {perc}%
                            </Typography>
                          </Typography>
                        </Box>
                        <Box sx={{ height: 5, borderRadius: 3, backgroundColor: "#f1f3f5", overflow: "hidden" }}>
                          <Box sx={{ width: `${perc}%`, height: "100%", backgroundColor: cor, transition: "width .3s" }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Paper>
              ))}
            </Box>
          )}

          {summary && summary.evaluationOverdue > 0 && (
            <Paper elevation={0} sx={{ ...cardSx, padding: "0.9rem 1.25rem", borderLeft: "4px solid #fd7e14" }}>
              <Typography sx={{ fontSize: 13 }}>
                <strong>{summary.evaluationOverdue}</strong> de {summary.patients} pacientes deste
                recorte estão com a reavaliação vencida.
              </Typography>
            </Paper>
          )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            ) : error ? (
              <Typography sx={{ p: 3, fontSize: 14, color: "#dc3545" }}>{error}</Typography>
            ) : (
              <>
                <ResultCount shown={total} />

                {items.length === 0 ? (
                  <Typography sx={{ p: 3, fontSize: 14, color: "#6c757d" }}>
                    Nenhum paciente com os filtros aplicados.
                  </Typography>
                ) : (
                  <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
                    <Box className="c4u-table__header">
                      {COLUNAS.map((c) => (
                        <Box key={c.titulo} className={`admin-plans-header-cell ${c.classe}`} sx={{ flex: c.flex, minWidth: 0 }}>
                          {c.titulo}
                        </Box>
                      ))}
                    </Box>

                    {items.map((item) => {
                      const vencida =
                        item.nextEvaluationDue !== null &&
                        item.nextEvaluationDue.slice(0, 10) < new Date().toISOString().slice(0, 10);
                      return (
                        <Box key={item.patientId} className="c4u-table__row">
                          <Box className="admin-plans-cell" data-label="Paciente" sx={{ flex: COLUNAS[0].flex, minWidth: 0 }}>
                            <Typography className="admin-plans-name" sx={{ fontSize: 13, overflowWrap: "anywhere" }}>
                              {item.patientName}
                            </Typography>
                          </Box>
                          <Box
                            className={`admin-plans-cell ${COLUNAS[1].classe}`}
                            data-label="Identificação"
                            sx={{ flex: COLUNAS[1].flex, minWidth: 0, gap: 0.75 }}
                          >
                            <Marca ok={item.registerComplete} titulo="Cadastro" />
                            <Marca ok={item.hasPhoto} titulo="Foto" />
                            <Marca ok={item.hasActiveLink} titulo="Vínculo" />
                          </Box>
                          <Box
                            className={`admin-plans-cell ${COLUNAS[2].classe}`}
                            data-label="Base clínica"
                            sx={{ flex: COLUNAS[2].flex, minWidth: 0, gap: 0.75 }}
                          >
                            <Marca ok={item.hasAnamnesis} titulo="Anamnese" />
                            <Marca ok={item.hasDiagnosis} titulo="Diagnóstico" />
                            <Marca ok={item.hasPlanGoals} titulo="Plano" critico />
                          </Box>
                          <Box className={`admin-plans-cell ${COLUNAS[3].classe}`} data-label="Últ. presença" sx={{ flex: COLUNAS[3].flex, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{formatDate(item.lastPresence)}</Typography>
                          </Box>
                          <Box className={`admin-plans-cell ${COLUNAS[4].classe}`} data-label="Presenças/mês" sx={{ flex: COLUNAS[4].flex, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                              {item.presencesLastMonth}
                            </Typography>
                          </Box>
                          <Box className={`admin-plans-cell ${COLUNAS[5].classe}`} data-label="Últ. evolução" sx={{ flex: COLUNAS[5].flex, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{formatDate(item.lastEvolution)}</Typography>
                          </Box>
                          <Box className={`admin-plans-cell ${COLUNAS[6].classe}`} data-label="Próx. agenda" sx={{ flex: COLUNAS[6].flex, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{formatDate(item.nextSchedule)}</Typography>
                          </Box>
                          <Box className="admin-plans-cell" data-label="Reavaliação" sx={{ flex: COLUNAS[7].flex, minWidth: 0 }}>
                            <Typography
                              sx={{ fontSize: 12, color: vencida ? "#dc3545" : "#6c757d", fontWeight: vencida ? 700 : 400 }}
                            >
                              {formatDate(item.nextEvaluationDue)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {items.length > 0 && (
                  <StandardPagination
                    currentPage={page}
                    totalPages={Math.max(1, Math.ceil(total / pageSize))}
                    itemsPerPage={pageSize}
                    totalItems={total}
                    onPageChange={setPage}
                    onItemsPerPageChange={(n) => {
                      setPageSize(n);
                      setPage(1);
                    }}
                  />
                )}
              </>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportPatientTracking;
